import React, { useState, useEffect } from "react";
import {
    Card,
    Breadcrumb,
    Divider,
    Button,
    Form,
    Input,
    message,
    Popconfirm,
    Space,
    Row,
    Col,
    Spin,
    Empty,
    Typography,
    Descriptions,
} from "antd";
import {
    HomeOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { voiceIpAPIWithErrorHandling } from "../../../api/voiceIP/voiceIP";

const { Title, Text } = Typography;

export default function VoiceCallScreen() {
    const [loading, setLoading] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [form] = Form.useForm();

    // Fetch account data
    const fetchAccount = async () => {
        try {
            setLoading(true);
            const response = await voiceIpAPIWithErrorHandling.getAccount();
            setAccountData(response.data);
        } catch (error) {
            if (error.message.includes("404")) {
                setAccountData(null);
            } else {
                message.error(error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccount();
    }, []);

    // Handle create/update account
    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            await voiceIpAPIWithErrorHandling.updateOrCreate(values);
            message.success(
                accountData
                    ? "Cập nhật thông tin thành công!"
                    : "Tạo thông tin thành công!"
            );
            setEditMode(false);
            form.resetFields();
            fetchAccount();
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditMode(false);
        if (accountData) {
            form.setFieldsValue(accountData);
        } else {
            form.resetFields();
        }
    };

    // Start edit
    const startEdit = () => {
        setEditMode(true);
        if (accountData) {
            form.setFieldsValue(accountData);
        }
    };

    // Handle delete account
    const handleDelete = async () => {
        try {
            setLoading(true);
            await voiceIpAPIWithErrorHandling.deleteAccount();
            message.success("Xóa thông tin thành công!");
            setAccountData(null);
            form.resetFields();
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <Card className="shadow-sm">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            {
                                href: "/",
                                title: (
                                    <>
                                        <HomeOutlined /> Trang chủ
                                    </>
                                ),
                            },
                            {
                                title: "Thiết lập thông tin VoIP",
                            },
                        ]}
                    />
                </div>

                <Divider />

                {/* Header */}
                <div className="mb-6">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={4} className="mb-0">
                                {/* <PhoneOutlined className="mr-2" /> */}
                                Thông tin kết nối VoIP
                            </Title>
                        </Col>
                        <Col>
                            {accountData && !editMode && (
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={startEdit}
                                    >
                                        Chỉnh sửa
                                    </Button>
                                    <Popconfirm
                                        title="Xóa thông tin"
                                        description="Bạn có chắc chắn muốn xóa thông tin này không?"
                                        onConfirm={handleDelete}
                                        okText="Xóa"
                                        cancelText="Hủy"
                                        okType="danger"
                                    >
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                        >
                                            Xóa
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            )}
                            {!accountData && !editMode && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setEditMode(true)}
                                >
                                    Tạo thông tin mới
                                </Button>
                            )}
                        </Col>
                    </Row>
                </div>

                {/* Content */}
                <Spin spinning={loading}>
                    {!accountData && !editMode ? (
                        <Empty
                            description="Chưa có thông tin kết nối VoIP"
                            className="my-8"
                        />
                    ) : editMode ? (
                        <Form
                            form={form}
                            onFinish={handleSubmit}
                            layout="vertical"
                            initialValues={accountData}
                        >
                            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} sm={12} md={8} lg={8}>
                                        <Form.Item
                                            label={
                                                <Text strong className="text-base">
                                                    Mã kết nối (Client ID)
                                                </Text>
                                            }
                                            name="client_id"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Vui lòng nhập mã kết nối",
                                                },
                                                {
                                                    min: 2,
                                                    message: "Mã phải có ít nhất 2 ký tự",
                                                },
                                            ]}
                                        >
                                            <Input
                                                size="default"
                                                placeholder="Nhập mã kết nối"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={8} lg={8}>
                                        <Form.Item
                                            label={
                                                <Text strong className="text-base">
                                                    Mã người dùng (User UUID)
                                                </Text>
                                            }
                                            name="user_uuid"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Vui lòng nhập mã người dùng",
                                                },
                                                {
                                                    min: 2,
                                                    message: "Mã người dùng phải có ít nhất 2 ký tự",
                                                },
                                            ]}
                                        >
                                            <Input
                                                size="default"
                                                placeholder="Nhập mã người dùng"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={8} lg={8}>
                                        <Form.Item
                                            label={
                                                <Text strong className="text-base">
                                                    Số máy (Extension)
                                                </Text>
                                            }
                                            name="ext"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Vui lòng nhập số máy",
                                                },
                                                {
                                                    min: 2,
                                                    message: "Số máy phải có ít nhất 2 ký tự",
                                                },
                                            ]}
                                        >
                                            <Input
                                                size="default"
                                                placeholder="Nhập số máy"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider />
                                <Row justify="end">
                                    <Space>
                                        <Button onClick={handleCancelEdit}>
                                            Hủy
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                        >
                                            {accountData ? "Cập nhật" : "Tạo mới"}
                                        </Button>
                                    </Space>
                                </Row>
                            </Card>
                        </Form>
                    ) : (
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                            <Descriptions
                                bordered
                                column={1}
                                size="middle"
                            >
                                <Descriptions.Item 
                                    label={<Text strong>Mã kết nối (Client ID)</Text>}
                                >
                                    <Text className="text-base">{accountData?.client_id || "_"}</Text>
                                </Descriptions.Item>

                                <Descriptions.Item 
                                    label={<Text strong>Mã người dùng (User UUID)</Text>}
                                >
                                    <Text className="text-base">{accountData?.user_uuid || "_"}</Text>
                                </Descriptions.Item>

                                <Descriptions.Item 
                                    label={<Text strong>Số máy (Extension)</Text>}
                                >
                                    <Text className="text-base">{accountData?.ext || "_"}</Text>
                                </Descriptions.Item>

                                <Descriptions.Item 
                                    label={<Text strong>Ngày tạo</Text>}
                                >
                                    <Text type="secondary">
                                        {accountData?.created_at 
                                            ? new Date(accountData.created_at).toLocaleString("vi-VN")
                                            : "_"
                                        }
                                    </Text>
                                </Descriptions.Item>

                                <Descriptions.Item 
                                    label={<Text strong>Cập nhật lần cuối</Text>}
                                >
                                    <Text type="secondary">
                                        {accountData?.updated_at
                                            ? new Date(accountData.updated_at).toLocaleString("vi-VN")
                                            : "_"
                                        }
                                    </Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    )}
                </Spin>
            </Card>
        </div>
    );
}